import { Chip, Tooltip, Typography } from "@mui/material";
import PostAddIcon from "@mui/icons-material/PostAdd";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { i18n } from "@/utils/i18n";
import { formatPersonalNumber } from "@/utils/helpers";
import ActionsColumn from "./actions-column";
import RegisterColumn from "./register-column";
import { ComplementStatus, ReportsList } from "@/types/report";

const getVariantLabel = (variant: string) => {
    if (variant === "arsrakning") return i18n.t("reportVariant.arsrakning");
    if (variant === "slutrakning") return i18n.t("reportVariant.slutrakning");
    return i18n.t("reportVariant.forteckning");
};

const useTableHelper = (reports: ReportsList, search: string) => {
    const columns: GridColDef[] = [
        {
            minWidth: 150,
            flex: 0.95,
            resizable: false,
            field: "name",
            headerName: i18n.t("reportColumn.principal"),
            headerClassName: "super-app-theme--header",
        },
        {
            minWidth: 148,
            flex: 0.9,
            resizable: false,
            field: "personalNumber",
            headerName: i18n.t("reportColumn.personalID"),
            align: "center",
            headerAlign: "center",
            headerClassName: "super-app-theme--header",
            renderCell: (params: GridRenderCellParams) => {
                return formatPersonalNumber(params.value);
            },
        },
        {
            width: 72,
            minWidth: 72,
            maxWidth: 72,
            resizable: false,
            sortable: false,
            field: "variant",
            headerName: i18n.t("reportColumn.type"),
            align: "center",
            headerAlign: "center",
            headerClassName: "super-app-theme--header",
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Chip
                        label={getVariantLabel(params.value)}
                        size="small"
                        sx={{
                            minWidth: 42,
                            borderRadius: "8px",
                            border: "1px solid #c7ccd1",
                            backgroundColor: "#f8fafc",
                            fontWeight: 700,
                        }}
                    />
                );
            },
        },
        {
            minWidth: 148,
            flex: 0.9,
            resizable: false,
            field: "trusteePersonalNumber",
            headerName: i18n.t("reportColumn.trustee"),
            align: "center",
            headerAlign: "center",
            headerClassName: "super-app-theme--header",
            renderCell: (params: GridRenderCellParams) => {
                return params.value ? formatPersonalNumber(params.value) : "-";
            },
        },
        {
            width: 124,
            minWidth: 124,
            maxWidth: 124,
            resizable: false,
            field: "startDate",
            headerName: i18n.t("reportColumn.reportStart"),
            align: "center",
            headerAlign: "center",
            headerClassName: "super-app-theme--header super-app-theme--date-start-header",
            cellClassName: "super-app-theme--date-start-cell",
            renderCell: (params: GridRenderCellParams) => {
                return Intl.DateTimeFormat("sv-SE").format(new Date(params.value));
            },
        },
        {
            width: 124,
            minWidth: 124,
            maxWidth: 124,
            resizable: false,
            field: "endDate",
            headerName: i18n.t("reportColumn.reportEnd"),
            align: "center",
            headerAlign: "center",
            headerClassName: "super-app-theme--header super-app-theme--date-mid-header",
            cellClassName: "super-app-theme--date-mid-cell",
            renderCell: (params: GridRenderCellParams) => {
                return Intl.DateTimeFormat("sv-SE").format(new Date(params.value));
            },
        },
        {
            width: 128,
            minWidth: 128,
            maxWidth: 128,
            resizable: false,
            field: "receivedAt",
            headerName: i18n.t("reportColumn.receivedAt"),
            align: "center",
            headerAlign: "center",
            headerClassName: "super-app-theme--header super-app-theme--date-end-header",
            cellClassName: "super-app-theme--date-end-cell",
            renderCell: (params: GridRenderCellParams) => {
                return Intl.DateTimeFormat("sv-SE").format(new Date(params.value));
            },
        },
        {
            width: 100,
            minWidth: 100,
            maxWidth: 100,
            resizable: false,
            sortable: false,
            field: "registered",
            headerName: i18n.t("reportColumn.registered"),
            align: "center",
            headerAlign: "center",
            headerClassName: "super-app-theme--header",
            renderCell: (params: GridRenderCellParams) => <RegisterColumn params={params} />,
        },
        {
            minWidth: 170,
            flex: 1.0,
            resizable: false,
            sortable: false,
            field: "status",
            headerName: i18n.t("reportColumn.status"),
            headerClassName: "super-app-theme--header",
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Typography sx={{ fontSize: "13px", fontWeight: 500, whiteSpace: "nowrap" }}>
                        {i18n.t(`${params.row.reviewStatus}`, { scope: "reportStatus" })}
                    </Typography>
                );
            },
        },
        {
            width: 50,
            minWidth: 50,
            maxWidth: 50,
            resizable: false,
            sortable: false,
            field: "complementStatus",
            headerName: "",
            align: "center",
            headerAlign: "center",
            headerClassName: "super-app-theme--header",
            renderCell: (params: GridRenderCellParams) => {
                const status = params.row.complementStatus as ComplementStatus;
                if (status === ComplementStatus.Received) {
                    return (
                        <Tooltip title={i18n.t("complementReceived")} arrow>
                            <PostAddIcon sx={{ color: "#e67e22", fontSize: 20 }} />
                        </Tooltip>
                    );
                }
                if (status === ComplementStatus.Requested) {
                    return (
                        <Tooltip title={i18n.t("completionRequested")} arrow>
                            <MailOutlineIcon sx={{ color: "#3498db", fontSize: 20 }} />
                        </Tooltip>
                    );
                }
                return null;
            },
        },
        {
            minWidth: 90,
            flex: 0.6,
            resizable: false,
            sortable: false,
            field: "actions",
            headerName: i18n.t("reportColumn.open"),
            align: "center",
            headerAlign: "center",
            headerClassName: "super-app-theme--header",
            renderCell: (params: GridRenderCellParams) => <ActionsColumn params={params} />,
        },
    ];

    const rows = reports
        .map(report => {
            const {
                id,
                eid,
                sid,
                oid,
                principal: { name, personalNumber },
                trustee,
                startDate,
                endDate,
                receivedAt,
                registered,
                reviewStatus,
                variant,
                complementStatus,
            } = report;

            return {
                id,
                eid,
                sid,
                oid,
                name,
                personalNumber,
                trusteePersonalNumber: trustee.personalNumber,
                startDate,
                endDate,
                receivedAt,
                registered,
                reviewStatus,
                variant,
                complementStatus: complementStatus ?? ComplementStatus.None,
                principal: { name, personalNumber },
            };
        })
        .filter(row =>
            [
                row.name,
                row.personalNumber,
                row.trusteePersonalNumber,
                row.variant,
                row.reviewStatus,
                row.startDate,
                row.endDate,
                row.receivedAt,
            ].some(value => String(value).toLowerCase().includes(search.toLowerCase())),
        );

    return { columns, rows };
};

export { useTableHelper };
